import type { IInitializeCampaign } from '~/modules/campaign';
import type { IInitializeCampaignUpdater } from '../hooks';
import type { Campaign } from '~/modules/campaign/types';
import type { ReactNode } from 'react';

import {
  useInitializeCampaignUpdater,
  useCampaignTemplateCheckState,
  useInitializeCampaign,
} from '../hooks';
import { useMemo, createContext, useContext, useRef, useState, useCallback } from 'react';

export interface ICampaignContext extends IInitializeCampaign, IInitializeCampaignUpdater {
  isShowDiscard: boolean;
  isDisabledEditing: boolean;
  /** A typed URL whose blur confirm is still pending: Save confirms it now, Discard drops it. */
  pendingEditRef: React.MutableRefObject<(() => Promise<void>) | undefined>;
  /** Bumped on every Discard, so fields holding unconfirmed input can reset with it. */
  discardCount: number;
}
export const CampaignContext = createContext<ICampaignContext | undefined>(undefined);

export interface ICampaignProvider<T extends Campaign> {
  children: ReactNode;
  campaignId?: T['id'];
  campaign?: T;
}

export const CampaignProvider = <T extends Campaign>({
  children,
  campaign,
  campaignId,
}: ICampaignProvider<T>) => {
  const initializeCampaign = useInitializeCampaign({
    campaignId,
    campaign,
  });
  const { isShowDiscard, isDisabledEditing } = useCampaignTemplateCheckState(
    initializeCampaign.campaign,
  );
  const initializeCampaignUpdater = useInitializeCampaignUpdater({
    initializeCampaign,
    isShowDiscard,
    handleSetCampaignData: initializeCampaign.handleSetCampaignData,
  });
  const pendingEditRef = useRef<() => Promise<void>>();
  const [discardCount, setDiscardCount] = useState(0);

  const { handleDiscardCampaign: discardCampaign } = initializeCampaign;
  const handleDiscardCampaign = useCallback(() => {
    discardCampaign();
    setDiscardCount((count) => count + 1);
  }, [discardCampaign]);

  const contextValue = useMemo<ICampaignContext>(
    () => ({
      ...initializeCampaign,
      ...initializeCampaignUpdater,
      isShowDiscard,
      isDisabledEditing,
      handleDiscardCampaign,
      pendingEditRef,
      discardCount,
    }),
    [
      isShowDiscard,
      isDisabledEditing,
      initializeCampaign,
      initializeCampaignUpdater,
      handleDiscardCampaign,
      discardCount,
    ],
  );

  return <CampaignContext.Provider value={contextValue}>{children}</CampaignContext.Provider>;
};

export const useCampaignContext = () => {
  const context = useContext(CampaignContext);
  if (!context) {
    throw new Error('GEMX_CONTEXT_IS_NOT_DEFINED');
  }
  return context;
};
